'use client';

import { SupportedLanguages, useTranslation } from '@/hooks/useTranslation';
import RouteChangeScript from '@/components/common/RouteChangeScript';
import SiteFrequentlyAskedQuestionsComponent from '@/components/frequently-asked-questions/SiteFrequentlyAskedQuestionsComponent';
import VideoBotIntegration from '@/components/blocks/VideoBotIntegration';
import widgetConfig, { WidgetConfigPages } from '@/utils/widgetConfigs';
import { BiRocket, BiTime, BiTrendingUp } from 'react-icons/bi';
import { HeroBots } from '@/components/blocks/HeroBots';
import React from 'react';
import { AiOutlineThunderbolt } from 'react-icons/ai';
import { FiExternalLink } from 'react-icons/fi';
import WhyChooseThisBot, { ColorDefaultChoice } from '@/components/blocks/WhyChooseThisBot';
import ChatbotsNewFeatures from '@/components/blocks/ChatbotsNewFeatures';
import { HiOutlineChartBar, HiOutlineClock, HiOutlineRocketLaunch } from 'react-icons/hi2';
import BlockDescriptionImageRight from '@/components/blocks/BlockDescriptionImageRight';
import { RiSparkling2Line } from 'react-icons/ri';
import BlockDescriptionImageLeft from '@/components/blocks/BlockDescriptionImageLeft';
import { CtaTargetType } from '@/components/buttons/LouiseButton';
import ReadyTransformBusiness from '@/components/blocks/ReadyTransformBusiness';
import { TbExchange, TbTargetArrow } from 'react-icons/tb';
import { WatchButtonCtaTargetType } from '@/components/buttons/LouiseWatchNowButton';
import GeneralChatbotBenefits from '@/components/blocks/GeneralChatbotBenefits';
import SectionIntegration from '@/components/sectionIntegration/SectionIntegration';
import { HiOutlineClipboardList } from 'react-icons/hi';
import { AnimatedGradientWrapper } from '@/components/background/AnimatedGradientWrapper';

interface PageContentProps {
  locale: string;
}

export default function PageContent({ locale }: PageContentProps) {
  const { t } = useTranslation(locale);
  const hero = {
    content: {
      badge: t('features/videoScenario/hero-badge'),
      title: t('features/videoScenario/hero-title'),
      description: t('features/videoScenario/hero-subtitle'),
      cta: t('features/videoScenario/hero-cta'),
      trustedBy: t('features/videoScenario/trusted-by'),
      img: 'https://cdn.1cdn.app/application/LOUISEBOT/25071220582748_videobot_widget.png',
      icon: TbTargetArrow,
      ctaTarget: 'register' as CtaTargetType,
    },
    benefits: [
      { icon: <AiOutlineThunderbolt className="tw-w-5 tw-h-5" />, text: t('features/videoScenario/hero-benefit1') },
      { icon: <TbTargetArrow className="tw-w-5 tw-h-5" />, text: t('features/videoScenario/hero-benefit2') },
    ],
    stats: [
      { value: '67%', label: t('features/videoScenario/focus1') },
      { value: 'x3.4', label: t('features/videoScenario/focus2') },
      { value: '40%', label: t('features/videoScenario/focus3') },
    ],
  };

  const sections = t('features/videoScenario/WhyChooseVideoBot/sections', { returnObjects: true }) as Array<any>;

  const whyChooseThisBot = [
    {
      iconicText: t(sections[0].improvement),
      title: t(sections[0].title),
      description: t(sections[0].description),
      icon: <BiTrendingUp className="tw-w-10 tw-h-10 tw-text-white" />,
      outlineIcon: <HiOutlineChartBar className="tw-w-6 tw-h-6 tw-text-purple-600" />,
      metricLabel: t(sections[0].metricLabel),
      button: t(sections[0].button),
      metric: t(sections[0].metric),
      colorFrom: 'pink' as ColorDefaultChoice,
      colorTo: 'purple' as ColorDefaultChoice,
    },
    {
      iconicText: t(sections[1].improvement),
      title: t(sections[1].title),
      description: t(sections[1].description),
      icon: <BiTime className="tw-w-10 tw-h-10 tw-text-white" />,
      outlineIcon: <HiOutlineClock className="tw-w-6 tw-h-6 tw-text-blue-600" />,
      metricLabel: t(sections[1].metricLabel),
      button: t(sections[1].button),
      metric: t(sections[1].metric),
      colorFrom: 'blue' as ColorDefaultChoice,
      colorTo: 'cyan' as ColorDefaultChoice,
    },
    {
      iconicText: t(sections[2].improvement),
      title: t(sections[2].title),
      description: t(sections[2].description),
      icon: <BiRocket className="tw-w-10 tw-h-10 tw-text-white" />,
      outlineIcon: <HiOutlineRocketLaunch className="tw-w-6 tw-h-6 tw-text-green-600" />,
      metricLabel: t(sections[2].metricLabel),
      button: t(sections[2].button),
      metric: t(sections[2].metric),
      colorFrom: 'green' as ColorDefaultChoice,
      colorTo: 'emerald' as ColorDefaultChoice,
    },
  ];

  const footerSectionBlock = t('features/videoScenario/NewFeaturesForBuildingVideobots/footerSectionBlock', {
    returnObjects: true,
  }) as Array<any>;
  const newFeatures = {
    section: {
      badge: t('features/videoScenario/NewFeaturesForBuildingVideobots/badge'),
      title: t('features/videoScenario/NewFeaturesForBuildingVideobots/title'),
      description: t('features/videoScenario/NewFeaturesForBuildingVideobots/description'),
      subtitle: t('features/videoScenario/NewFeaturesForBuildingVideobots/subtitle'),
      badgeCatchEye: {
        title: t('features/videoScenario/NewFeaturesForBuildingVideobots/badgeCatchEye/title'),
        description: t('features/videoScenario/NewFeaturesForBuildingVideobots/badgeCatchEye/description'),
      },
      footerSectionBlock: footerSectionBlock,
    },
    features: [
      {
        title: t('features/videoScenario/NewFeaturesForBuildingVideobots/features/feature1/title'),
        description: t('features/videoScenario/NewFeaturesForBuildingVideobots/features/feature1/description'),
        subtitle: t('features/videoScenario/NewFeaturesForBuildingVideobots/features/feature1/subtitle'),
        img: 'https://cdn.1cdn.app/application/LOUISEBOT/2502081232447_make_an_HTTP_request-min.png',
        icon: <FiExternalLink className="tw-w-6 tw-h-6 tw-text-white" />,
      },
      {
        title: t('features/videoScenario/NewFeaturesForBuildingVideobots/features/feature2/title'),
        description: t('features/videoScenario/NewFeaturesForBuildingVideobots/features/feature2/description'),
        subtitle: t('features/videoScenario/NewFeaturesForBuildingVideobots/features/feature2/subtitle'),
        img: 'https://cdn.1cdn.app/application/LOUISEBOT/25011022052642_conditions-min.png',
        icon: <HiOutlineClipboardList className="tw-w-6 tw-h-6 tw-text-white" />,
      },
      {
        title: t('features/videoScenario/NewFeaturesForBuildingVideobots/features/feature3/title'),
        description: t('features/videoScenario/NewFeaturesForBuildingVideobots/features/feature3/description'),
        subtitle: t('features/videoScenario/NewFeaturesForBuildingVideobots/features/feature3/subtitle'),
        img: 'https://cdn.1cdn.app/application/LOUISEBOT/25020812332841_show_a_form-min.png',
        icon: <TbExchange className="tw-w-6 tw-h-6 tw-text-white" />,
      },
    ],
  };

  return (
    <div className="video-chatbot-page">
      {/* Dedicated widget */}
      <RouteChangeScript code={widgetConfig.getWidgetCode(WidgetConfigPages.DiscoverVideobot, locale)} />

      {/* Hero Section - Full Width */}
      <HeroBots benefits={hero.benefits} stats={hero.stats} content={hero.content} locale={locale} />

      <AnimatedGradientWrapper>
        <section className="tw-relative tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
          <WhyChooseThisBot
            title={t('features/videoScenario/WhyChooseVideoBot/title')}
            subtitle={t('features/videoScenario/WhyChooseVideoBot/subtitle')}
            description={t('features/videoScenario/WhyChooseVideoBot/description')}
            sections={whyChooseThisBot}
          />
          <ChatbotsNewFeatures
            features={newFeatures.features}
            subtitle={newFeatures.section.subtitle}
            description={newFeatures.section.description}
            title={newFeatures.section.title}
            badge={newFeatures.section.badge}
            badgeCatchEye={newFeatures.section.badgeCatchEye}
            footerSectionBlock={newFeatures.section.footerSectionBlock}
            locale={locale as SupportedLanguages}
          />
        </section>

        <section className="tw-relative tw-pt-10 lg:tw-pt-10 tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
          <div className="tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
            <div className="tw-space-y-10">
              <BlockDescriptionImageRight
                badge={t('features/videoScenario/simple-setup-badge')}
                badgeIcon={<RiSparkling2Line className="tw-w-4 tw-h-4" />}
                title={t('features/videoScenario/bloc1/title')}
                description={t('features/videoScenario/bloc1/description')}
                btnColor="blue"
                cta={t('features/videoScenario/bloc1/cta')}
                image={'https://cdn.1cdn.app/application/LOUISEBOT/24120308082357_page_video_easy_to_integrate.png'}
                link="register"
                locale={locale}
              />
              <BlockDescriptionImageLeft
                title={t('features/videoScenario/bloc2/title')}
                description={t('features/videoScenario/bloc2/description')}
                btnColor="blue"
                cta={t('features/videoScenario/bloc2/cta')}
                image={
                  'https://cdn.1cdn.app/application/LOUISEBOT/24120308072789_page_video_customizable_to_suit_your_image.png'
                }
                link="register"
                locale={locale}
              />
            </div>
          </div>
        </section>

        <GeneralChatbotBenefits locale={locale} />

        <section className="tw-relative tw-pt-10 lg:tw-pt-10 tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
          <VideoBotIntegration locale={locale} />
        </section>

        <section className="tw-relative tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
          {/* Ready to transform your business */}
          <ReadyTransformBusiness locale={locale} />
        </section>

        <section className="tw-relative tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
          <SectionIntegration locale={locale} />
        </section>

        <SiteFrequentlyAskedQuestionsComponent locale={locale} session="VideoChatbotFAQs" />
      </AnimatedGradientWrapper>
    </div>
  );
}
